import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, FolderCog, RefreshCcw, Save, Upload } from "lucide-react";
import { type ComponentProps, useState } from "react";
import {
  capitalize,
  formatDate,
  getFieldOptionDisplayName,
  isOfflinePlatform,
} from "shared";
import { toast } from "sonner";
import z from "zod";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FormStepSeparator } from "@/components/ui/form-step-seperator";
import {
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAppForm } from "@/hooks/useCreateForm";
import { cn } from "@/lib/clientUtils";
import { ManualAccountingSync } from "./--components/manual-sync";

const editAccountingFieldValidator = z.object({
  defaultSelectionId: z
    .string()
    .nullable()
    .transform((val) => (val === "" ? null : val)),
  displayName: z.string().min(1, "Display name is required"),
  requirementLevel: z.enum([
    "required",
    "optional",
    "disabled",
    "alwaysRequired",
  ]),
});

export const Route = createFileRoute(
  "/_authed/_org/accounting/settings/fields/$id",
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { trpc, queryClient } = Route.useRouteContext();
  const { id } = Route.useParams();
  const [isEditing, setIsEditing] = useState(false);

  const { data: fields } = useSuspenseQuery(
    trpc.accounting.getAccountingFields.queryOptions(),
  );

  const { data: accountingData } = useSuspenseQuery(
    trpc.accounting.get.queryOptions(),
  );
  const isOffline = isOfflinePlatform(accountingData?.type);

  const { mutate: syncFieldOptions, isPending: isSyncAccountSyncPending } =
    useMutation(
      trpc.accounting.syncFieldOptions.mutationOptions({
        onSuccess: () => {
          queryClient.invalidateQueries(
            trpc.accounting.getAccountingFields.queryOptions(),
          );
        },
      }),
    );

  const { mutate: editField, isPending: isEditPending } = useMutation(
    trpc.accounting.editAccountingField.mutationOptions({
      onError: (error) => {
        toast.error("Failed to update field", {
          description: error.message,
        });
      },
      onSuccess: () => {
        queryClient.invalidateQueries(
          trpc.accounting.getAccountingFields.queryOptions(),
        );
        toast.success("Field updated successfully");
        setIsEditing(false);
      },
    }),
  );

  const { mutate: toggleFieldEnabled } = useMutation(
    trpc.accounting.editAccountingFieldOption.mutationOptions({
      onError: (error) => {
        toast.error("Failed to update field", {
          description: error.message,
        });
      },
      onSuccess: () => {
        queryClient.invalidateQueries(
          trpc.accounting.getAccountingFields.queryOptions(),
        );

        setIsEditing(false);
      },
    }),
  );

  const currentField = fields.find((f) => f.id === id);

  if (!currentField) {
    return <div>Field not found</div>;
  }

  const enabledOptions =
    currentField.options?.filter((option) => option.status === "enabled") || [];

  const requirementLevelOptions = [
    {
      description: "Make this field required before sync",
      label: "Required",
      value: "required",
    },
    {
      description:
        "Show this field, but do not require a value to be selected before sync",
      label: "Optional",
      value: "optional",
    },
    {
      description: "Hide this accounting field in the Givefront UI",
      label: "Disabled",
      value: "disabled",
    },
  ];

  const defaultSelectionOptions = [
    { label: "No default", value: "" },
    ...enabledOptions.map((option) => ({
      label: getFieldOptionDisplayName(option),
      value: option.id,
    })),
  ];

  const form = useAppForm({
    defaultValues: {
      defaultSelectionId: currentField.defaultSelectionId || "",
      displayName: currentField.displayName || currentField.name,
      requirementLevel: currentField.requirementLevel,
    } as z.infer<typeof editAccountingFieldValidator>,
    onSubmit: ({ value }) => {
      editField({
        fieldId: currentField.id,
        ...value,
        // Tanstack form doesn't apply zod transforms, so this is needed to transform empty string into null
        defaultSelectionId:
          value.defaultSelectionId !== "" ? value.defaultSelectionId : null,
      });
    },
    validators: {
      onSubmit: editAccountingFieldValidator,
    },
  });

  const SyncButton = (props: ComponentProps<typeof Button>) =>
    isOffline ? (
      <Button variant="outline" type="button" asChild {...props}>
        <ManualAccountingSync>
          <Upload className="size-3.5" />
          Re-import Chart of Accounts
        </ManualAccountingSync>
      </Button>
    ) : (
      <Button
        disabled={isSyncAccountSyncPending}
        onClick={() => {
          syncFieldOptions({
            fieldId: currentField.id,
          });
        }}
        type="button"
        variant="outline"
        {...props}
      >
        <RefreshCcw
          className={cn({
            "animate-spin": isSyncAccountSyncPending,
          })}
        />
        {isSyncAccountSyncPending ? "Syncing" : "Sync Field Options"}
      </Button>
    );

  return (
    <div className="flex h-full flex-col">
      <div className={"border-border border-b pt-6 pb-4"}>
        <Button variant="text" className="m-0 mx-6 h-4 w-16 p-0" asChild>
          <Link to="/accounting/settings">
            <ArrowLeft className="mr-2 h-4 w-4" />
            <span>Back</span>
          </Link>
        </Button>
        <SheetHeader>
          <SheetTitle className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {currentField.displayName || currentField.name}

              <Badge
                variant={
                  currentField.requirementLevel === "required" ||
                  currentField.requirementLevel === "alwaysRequired"
                    ? "orange"
                    : "yellow"
                }
              >
                {currentField.requirementLevel === "alwaysRequired"
                  ? "Always Required"
                  : capitalize(currentField.requirementLevel)}
              </Badge>
            </div>

            <div className="flex gap-2">
              <SyncButton />

              <Button
                onClick={() => setIsEditing(!isEditing)}
                type="button"
                variant={isEditing ? "secondary" : "outline"}
              >
                {isEditing ? "Cancel" : "Edit Field"}
              </Button>
            </div>
          </SheetTitle>
          <SheetDescription>
            Last synced:{" "}
            {currentField.lastSyncedAt
              ? formatDate(currentField.lastSyncedAt, "date-time")
              : "Never"}
          </SheetDescription>
        </SheetHeader>
      </div>

      <div className="flex min-h-0 grow flex-col overflow-y-auto">
        {isEditing && (
          <form
            className="flex min-h-0 grow flex-col"
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              form.handleSubmit();
            }}
          >
            <div className="mb-6 flex flex-col gap-4 p-6 pb-3">
              <FormStepSeparator description="Field Settings" />

              <form.AppField
                name="displayName"
                children={(field) => (
                  <field.TextField label="Display Name" required={true} />
                )}
              />

              {/* Only show the requirement level selector if it is not a field that is alwaysRequired */}
              {currentField.requirementLevel !== "alwaysRequired" && (
                <form.AppField
                  name="requirementLevel"
                  children={(field) => (
                    <field.SelectField
                      label="Requirement Level"
                      options={requirementLevelOptions}
                      required={true}
                    />
                  )}
                />
              )}

              {currentField.defaultable && (
                <form.AppField
                  name="defaultSelectionId"
                  children={(field) => (
                    <field.SelectSearchField
                      label="Default Selection"
                      descriptionStyle="below"
                      description="This option will be filled in automatically"
                      options={defaultSelectionOptions}
                      required={false}
                    />
                  )}
                />
              )}

              <div className="flex min-h-16 items-center justify-end">
                <Button
                  type="submit"
                  disabled={isEditPending}
                  className="font-semibold"
                >
                  {isEditPending ? (
                    <>
                      <RefreshCcw className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="mr-2 h-4 w-4" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </div>
          </form>
        )}

        {currentField.options.length === 0 && (
          <div className="flex grow flex-col items-center justify-center">
            <div className="rounded-md bg-neutral-100 p-4">
              <FolderCog className="size-8 text-neutral-600" />
            </div>
            <h3 className="mt-6 text-neutral-900 text-xl">
              No Options Available
            </h3>

            <p className="mt-2 mb-4 text-neutral-700 text-sm">
              This field currently has no options available for selection.
            </p>

            <SyncButton size="sm" />
          </div>
        )}

        {!isEditing && currentField.options.length !== 0 && (
          <div className="p-6">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50 text-xs [&>th]:font-medium! [&>th]:text-gray-500">
                  <TableHead className="w-[60%] px-2">Option Name</TableHead>
                  <TableHead className="w-[20%] px-2">Status</TableHead>
                  <TableHead className="w-[20%] px-2">Enabled</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {currentField.options
                  .sort((a, b) => a.name.localeCompare(b.name))
                  ?.map((option) => (
                    <TableRow key={option.id}>
                      <TableCell className="font-medium">
                        {getFieldOptionDisplayName(option)}
                      </TableCell>

                      <TableCell>
                        <Badge variant={option.enabled ? "green" : "gray"}>
                          {option.enabled ? "Enabled" : "Hidden"}
                        </Badge>
                      </TableCell>

                      <TableCell>
                        <Switch
                          checked={option.enabled}
                          onCheckedChange={(value) =>
                            toggleFieldEnabled({
                              enabled: value,
                              fieldOptionId: option.id,
                            })
                          }
                        />
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
